<template>
  <div class="connection-status-list mb-5 mx-2">
    <h1 class="mb-3">Connection Status List</h1>
    <div class="mb-3">
      <div class="card mb-3 table-card">
        <div class="card-header">
          <div class="row">
            <div class="col-md-6 col-12 d-flex ">
              <h3 class="mt-2">Connections ({{ local_num_connections }})</h3>
              <span class="text-muted mt-2 ms-3">
                <a href="#" @click.prevent="showImportErrors">
                  {{ local_num_errors }} Import Errors
                </a>
              </span>
            </div>
            <div class="col-md-6 d-none d-md-block">
              <div class="d-flex justify-content-end gap-2">
                <div class="position-relative">
                  <input
                    type="text"
                    placeholder="Search by Name, Client, or Integration..."
                    v-model="search_key"
                    @input="debouncedSearchOptions"
                    class="search-box form-control me-1 py-2"
                   aria-label="Search by Name, Client, or Integration...">
                  <i class="fa fa-search search-placeholder-icon" v-show="search_key === ''"></i>
                </div>
                <button class="btn btn-secondary me-1 py-0 dropdown-toggle dropdown-toggle-no-btn" data-bs-toggle="dropdown" data-bs-auto-close="outside" aria-expanded="false">
                  <img class="icon" src="/img/icons/avid-icons/filter-funnel-01.svg" alt="">Filters ({{ activeFilterCount }})
                </button>
                <div class="dropdown-menu dropdown-menu-primary p-3" style="max-height: 450px; overflow-y: scroll;">
                  <div v-if="activeFilterCount > 0" class="mb-3">
                    <a href="#" @click.prevent="clearAllFilters" class="text-primary">
                      <i class="fa fa-times me-1"></i>Clear All Filters
                    </a>
                    <hr class="my-2">
                  </div>
                  <label class="form-label">Client:</label>
                  <select class="form-select mb-2" v-model="filters.client_id" @change="handleFilterSelection" aria-label="Client:">
                    <option value="">All Clients</option>
                    <option v-for="c in available_clients" :key="c.id" :value="c.id">{{ c.name }}</option>
                  </select>

                  <label class="form-label">Integration:</label>
                  <select class="form-select mb-2" v-model="filters.integration_id" @change="handleFilterSelection" aria-label="Integration:">
                    <option value="">All Integrations</option>
                    <option v-for="i in available_integrations" :key="i.id" :value="i.id">{{ i.display_name || i.name }}</option>
                  </select>

                  <label class="form-label">Sync Schedule:</label>
                  <select class="form-select mb-2" v-model="filters.sync_schedule" @change="handleFilterSelection" aria-label="Sync Schedule:">
                    <option value="">All</option>
                    <option v-for="s in available_sync_schedules" :key="s.id" :value="s.id">{{ s.name }}</option>
                  </select>

                  <label class="form-label">Import Status:</label>
                  <select class="form-select mb-2" v-model="filters.import_status" @change="handleFilterSelection" aria-label="Import Status:">
                    <option value="">All</option>
                    <option v-for="status in available_import_statuses" :key="status.id" :value="status.id">{{ status.name }}</option>
                  </select>

                  <label class="form-label">Pathway Status:</label>
                  <select class="form-select mb-2" v-model="filters.export_status" @change="handleFilterSelection" aria-label="Pathway Status:">
                    <option value="">All</option>
                    <option v-for="status in available_export_statuses" :key="status.id" :value="status.id">{{ status.name }}</option>
                  </select>

                  <label class="form-label">Created By:</label>
                  <select class="form-select mb-2" v-model="filters.created_by_id" @change="handleFilterSelection" aria-label="Created By:">
                    <option value="">All Users</option>
                    <option v-for="u in available_users" :key="u.id" :value="u.id">{{ u.name }}</option>
                  </select>

                  <label class="form-label">Date Mismatch:</label>
                  <select class="form-select mb-2" v-model="filters.date_mismatch" @change="handleFilterSelection" aria-label="Date Mismatch:">
                    <option value="">All</option>
                    <option value="mismatch">Sync ≠ Refresh Time</option>
                    <option value="match">Sync = Refresh Time</option>
                  </select>
                </div>

              </div>
            </div>
          </div>
        </div>
        <div class="card-body p-0">
          <div class="table-width-responsive">
            <table class="table table-with-pagination mb-0">
              <thead>
                <tr class="table-header-row">
                  <th @click.prevent="sortByKey('name')" class="d-table-cell">
                    <div class="d-flex flex-row">
                      Name
                      <div class="d-flex flex-column">
                        <img class="sort-key" src="/img/icons/avid-icons/chevron-up.svg" :class="{ 'sort-key-active': sort_key === 'name' && sort_order === 'asc' }" alt="">
                        <img class="sort-key" src="/img/icons/avid-icons/chevron-down.svg" :class="{ 'sort-key-active': sort_key === 'name' && sort_order === 'des' }" alt="">
                      </div>
                    </div>
                  </th>
                  <th @click.prevent="sortByKey('client_name')" class="d-none d-lg-table-cell">
                    <div class="d-flex flex-row">
                      Client
                      <div class="d-flex flex-column">
                        <img class="sort-key" src="/img/icons/avid-icons/chevron-up.svg" :class="{ 'sort-key-active': sort_key === 'client_name' && sort_order === 'asc' }" alt="">
                        <img class="sort-key" src="/img/icons/avid-icons/chevron-down.svg" :class="{ 'sort-key-active': sort_key === 'client_name' && sort_order === 'des' }" alt="">
                      </div>
                    </div>
                  </th>
                  <th @click.prevent="sortByKey('sync_schedule')" class="d-none d-md-table-cell">
                    <div class="d-flex flex-row">
                      Sync Schedule
                      <div class="d-flex flex-column">
                        <img class="sort-key" src="/img/icons/avid-icons/chevron-up.svg" :class="{ 'sort-key-active': sort_key === 'sync_schedule' && sort_order === 'asc' }" alt="">
                        <img class="sort-key" src="/img/icons/avid-icons/chevron-down.svg" :class="{ 'sort-key-active': sort_key === 'sync_schedule' && sort_order === 'des' }" alt="">
                      </div>
                    </div>
                  </th>
                  <th @click.prevent="sortByKey('last_synced_at')" class="d-none d-md-table-cell">
                    <div class="d-flex flex-row">
                      Last Sync Time
                      <div class="d-flex flex-column">
                        <img class="sort-key" src="/img/icons/avid-icons/chevron-up.svg" :class="{ 'sort-key-active': sort_key === 'last_synced_at' && sort_order === 'asc' }" alt="">
                        <img class="sort-key" src="/img/icons/avid-icons/chevron-down.svg" :class="{ 'sort-key-active': sort_key === 'last_synced_at' && sort_order === 'des' }" alt="">
                      </div>
                    </div>
                  </th>
                  <th @click.prevent="sortByKey('client_last_sync_date')" class="d-none d-md-table-cell">
                    <div class="d-flex flex-row">
                      Last Refresh Time
                      <div class="d-flex flex-column">
                        <img class="sort-key" src="/img/icons/avid-icons/chevron-up.svg" :class="{ 'sort-key-active': sort_key === 'client_last_sync_date' && sort_order === 'asc' }" alt="">
                        <img class="sort-key" src="/img/icons/avid-icons/chevron-down.svg" :class="{ 'sort-key-active': sort_key === 'client_last_sync_date' && sort_order === 'des' }" alt="">
                      </div>
                    </div>
                  </th>
                  <th class="d-table-cell">
                    <div class="d-flex flex-row justify-content-center">
                      Import Status
                    </div>
                  </th>
                  <th class="d-table-cell">
                    <div class="d-flex flex-row justify-content-center">
                      Pathway Status
                    </div>
                  </th>
                  <th @click.prevent="sortByKey('created_by_name')" class="d-none d-lg-table-cell">
                    <div class="d-flex flex-row">
                      Created By
                      <div class="d-flex flex-column">
                        <img class="sort-key" src="/img/icons/avid-icons/chevron-up.svg" :class="{ 'sort-key-active': sort_key === 'created_by_name' && sort_order === 'asc' }" alt="">
                        <img class="sort-key" src="/img/icons/avid-icons/chevron-down.svg" :class="{ 'sort-key-active': sort_key === 'created_by_name' && sort_order === 'des' }" alt="">
                      </div>
                    </div>
                  </th>
                  <th class="d-table-cell column-sort-disabled text-center pe-3" width="1">
										<div class="d-flex flex-row">
											Actions
										</div>
									</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="connection in list_shown" :key="connection.id">
                  <td class="d-table-cell align-start">
                    <div class="d-flex align-items-center text-wrap">
                      <a v-if="connection.client.url" :href="`/settings/${connection.client.url}/integrations?connection=${connection.id}`">
                        <img v-if="connection.integration && connection.integration.logo_url"
                           :src="connection.integration.logo_url"
                           :alt="connection.integration.display_name || connection.integration.name"
                           class="avatar rounded-circle avatar-sm bg-white me-2"
                      >
                        {{ connection.name }}
                      </a>
                      <span v-else>{{ connection.name }}</span>
                    </div>
                  </td>
                  <td class="d-none d-lg-table-cell">
                    <div class="d-flex align-items-center text-wrap">
                      <a v-if="connection.client" :href="`/settings/${connection.client.url}/integrations`">
                        <img v-if="connection.client.image" :src="connection.client.image" :alt="connection.client.name" class="avatar rounded-circle avatar-sm bg-white me-2">
                        <span>{{ connection.client?.name }}</span>
                      </a>

                    </div>
                  </td>
                  <td class="d-none d-md-table-cell">
                    {{ propercase(connection.sync_schedule) || '-' }}
                  </td>
                  <td class="d-none d-md-table-cell text-wrap">
                    <span v-if="connection.last_synced_at" :title="datetime(connection.last_synced_at)" data-bs-toggle="tooltip">
                      {{ utc_date(connection.last_synced_at) }}
                    </span>
                    <span v-else>-</span>
                  </td>
                  <td class="d-none d-md-table-cell text-wrap">
                    <span v-if="connection.client.last_sync_date" :title="datetime(connection.client.last_sync_date)" data-bs-toggle="tooltip">
                      {{ utc_date(connection.client.last_sync_date) }}
                    </span>
                    <span v-else>-</span>
                  </td>
                  <td class="d-table-cell text-center">
                    <i v-if="!connection.sync_error_msg" class="fas fa-check green"></i>
                    <i v-else class="fas fa-times text-danger" data-bs-toggle="tooltip" data-bs-placement="top"
                      :title="(connection.sync_error_at ? (connection.sync_error_msg + ' — ' + datetime(connection.sync_error_at)) : connection.sync_error_msg) + (connection.error_reference_id ? ' (Reference: ' + connection.error_reference_id + ')' : '')"></i>
                  </td>
                  <td class="d-table-cell text-center">
                    <i v-if="hasPathway(connection) && !hasPathwayError(connection)" class="fas fa-check green"></i>
                    <i v-else-if="hasPathway(connection)" class="fas fa-times text-danger" data-bs-toggle="tooltip" data-bs-placement="top"
                       :title="pathwayErrorTooltip(connection)"></i>
                  </td>
                  <td class="d-none d-lg-table-cell">
                    <div class="d-flex align-items-center text-wrap">
                      <img v-if="connection.created_by && connection.created_by.image" :src="connection.created_by.image" :alt="connection.created_by.name" class="avatar rounded-circle avatar-sm bg-white me-2">
                      <span>{{ connection.created_by?.name }}</span>
                    </div>
                  </td>
                  <td class="d-table-cell text-center">
                    <div class="dropdown d-flex justify-content-center">
                      <button class="btn btn-white btn-icon-only-md" data-bs-toggle="dropdown" aria-expanded="false" aria-label="Actions">
                        <img class="icon" src="/img/icons/avid-icons/dots-vertical.svg" data-bs-toggle="tooltip" title="Actions" alt="">
                      </button>
                      <ul class="dropdown-menu dropdown-menu-primary dropdown-menu-end">
                        <li v-if="canSync(connection)">
                          <a class="dropdown-item" href="#" @click.prevent="forceSync(connection)">
                            <img class="icon me-2" src="/img/icons/avid-icons/sync.svg" alt=""> Force Sync
                          </a>
                        </li>

                        <li v-if="connection.airbyte_v2_connection_id">
                          <a class="dropdown-item" :href="airbyteLink(connection)" target="_blank">
                            <img class="me-2" src="/img/integrations/airbyte_logo_square.svg" style="width: 24px; height: 24px;"> View in Airbyte
                          </a>
                        </li>
                        <li v-if="connection.dbt_job_id">
                          <a class="dropdown-item" :href="'https://zc630.us1.dbt.com/deploy/115789/projects/174191/jobs/'+connection.dbt_job_id" target="_blank">
                            <img class="me-2" src="/img/icons/avid-icons/dbt.webp" style="width: 24px; height: 24px;"> View in dbt
                          </a>
                        </li>

                        <li v-if="connection.dbt_job_id">
                          <a class="dropdown-item" href="#" @click.prevent="rerunDbtModel(connection.id)">
                            <img class="me-2" src="/img/icons/avid-icons/refresh-ccw-01.svg" style="width: 24px; height: 24px;" alt=""> Run dbt Model
                          </a>
                        </li>
                      </ul>
                    </div>
                  </td>
                </tr>
                <tr v-if="list_shown.length === 0">
                  <td colspan="10" class="text-center py-3">No connections found.</td>
                </tr>
              </tbody>
              <tfoot class="table-border-bottom-0">
                <tr>
                  <td colspan="100%" class="py-0">
                    <div class="table-pagination">
                      <div class="table-pagination--group d-none d-md-flex">
                        <div class="position-relative">
                          <select class="form-select" v-model.number="num_per_page" @change="reloadPagination()" aria-label="Items per page">
                            <option value="5000">ALL</option>
                            <option v-for="num in num_per_page_options" :key="num" :value="num">{{ num }}</option>
                          </select>
                          <div class="select-display-text">
                            Showing {{ num_per_page === 5000 ? 'ALL' : num_per_page }}
                          </div>
                        </div>
                      </div>
                      <div class="table-pagination--group d-none d-lg-flex">
                        {{ local_num_connections === 0 ? '0' : item_interval }} of {{ local_num_connections }}
                      </div>
                      <div class="table-pagination--group">
                        <button v-if="page_count > 1" class="btn btn-secondary btn-icon-only-md me-1" @click.prevent="moveToPrevPage()" :disabled="page_index === 0" data-bs-toggle="tooltip" title="Previous page">
                          <img class="icon" src="/img/icons/avid-icons/chevron-left.svg" alt="">
                        </button>
                        <div class="d-flex page-selector" v-if="page_index_array.length <= 3">
                          <button v-for="page in page_index_array" :key="page" class="btn btn-none btn-icon-only-md" :class="{ 'current-page': page === (page_index + 1) }" @click.prevent="moveToPage(page)">
                            {{ page }}
                          </button>
                        </div>
                        <div class="d-flex page-selector" v-else>
                          <button v-for="page in page_index_array.slice(0,2)" :key="page" class="btn btn-none btn-icon-only-md" :class="{ 'current-page': page === (page_index + 1) }" @click.prevent="moveToPage(page)">
                            {{ page }}
                          </button>
                          <button v-if="page_index > 2" class="btn btn-none btn-icon-only-md text-nowrap" disabled>
                          ...
                          </button>
                          <button v-if="page_index >= 2 && page_index < page_index_array.length -1" class="btn btn-none btn-icon-only-md current-page">
                            {{ page_index + 1 }}
                          </button>
                          <button v-if="page_index < page_index_array.length -2" class="btn btn-none btn-icon-only-md text-nowrap" disabled aria-hidden="true">
                          ...
                          </button>
                          <button :class="{ 'current-page': page_index === (page_index_array.length -1) }" class="btn btn-none btn-icon-only-md" @click.prevent="moveToPage(page_index_array.length)" :key="page_index_array.length">
                            {{ page_index_array.length }}
                          </button>
                        </div>
                        <button v-if="page_count > 1" class="btn btn-secondary btn-icon-only-md ms-1" @click.prevent="moveToNextPage()" :disabled="page_index === page_count -1" data-bs-toggle="tooltip" title="Next page">
                          <img class="icon" src="/img/icons/avid-icons/chevron-right.svg" alt="">
                        </button>
                      </div>
                    </div>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import tablePaginationMixin from '../../mixins/table_pagination';
import debounce from 'lodash.debounce';
import { useNotifications } from '../../composables/useNotifications';

export default {
  name: 'ConnectionStatusList',
  setup() {
    const { showNotification } = useNotifications();
    return {
      showNotification
    };
  },
  props: {
    connections: {
      type: Array,
      default: () => []
    },
    num_connections: {
      type: Number,
      default: 0
    },
    num_errors: {
      type: Number,
      default: 0
    }
  },
  mixins: [tablePaginationMixin],
  data() {
    return {
      filtered_connections: [],
      search_key: '',
      sort_key: 'name',
      sort_order: 'asc',
      local_num_connections: null,
      local_num_errors: null,

      filters: {
        client_id: '',
        integration_id: '',
        sync_schedule: '',
        import_status: '', // active|error
        export_status: '', // active|error (pathways)
        created_by_id: '',
        date_mismatch: ''
      },
      available_clients: [],
      available_integrations: [],
      available_sync_schedules: [],
      available_users: [],
      available_import_statuses: [],
      available_export_statuses: [],
    }
  },

  computed: {
    activeFilterCount() {
      let count = 0;
      if (this.filters.client_id) count++;
      if (this.filters.integration_id) count++;
      if (this.filters.sync_schedule) count++;
      if (this.filters.import_status) count++;
      if (this.filters.export_status) count++;
      if (this.filters.created_by_id) count++;
      if (this.filters.date_mismatch) count++;
      return count;
    }
  },

  watch: {
    connections: {
      handler(newVal) {
        if (newVal && newVal.length > 0) {
          this.filtered_connections = [...newVal];
          this.reloadPagination();
        }
      },
      immediate: true
    },
    num_connections: {
      handler(newVal) {
        this.local_num_connections = newVal;
      },
      immediate: true
    },
    num_errors: {
      handler(newVal) {
        this.local_num_errors = newVal;
      },
      immediate: true
    }
  },

  beforeMount() {
    this.local_num_connections = this.num_connections;
    this.local_num_errors = this.num_errors;
    this.debouncedSearchOptions = debounce(this.searchConnections, 500);
  },
  mounted() {
    this.filtered_connections = [...this.connections];
    this.initializeFilterOptions();
    this.reloadPagination();
    this.$nextTick(() => {
      this.initializeTooltips();
    });
  },
  updated() {
    this.$nextTick(() => {
      this.initializeTooltips();
    });
  },

  methods: {
    forceSync(connection) {
      // Mimic settings/ConnectionsSettingsSystems.vue syncConnection(conn)
      window.axios.get('/api/connection/' + connection.id + '/refresh')
        .then(() => {
          // Set a busy state similar to settings component
          connection.active_jobs = ["something"];
          this.$forceUpdate();
          window.Swal && Swal.fire({
            title: 'Success!',
            text: 'The connection will begin refreshing momentarily.',
            icon: 'success',
            customClass: { popup: 'swal-success' }
          });
        })
        .catch(error => {
          window.Swal && Swal.fire({
            title: 'Error!',
            text: error?.response?.data?.message || 'Unable to start sync.',
            icon: 'error',
            customClass: { popup: 'swal-error' }
          });
        });
    },
    airbyteLink(connection) {
      const workspaceId = '1ad4ae36-9516-4549-9a2b-b161029b4411';
      const connId = connection.airbyte_v2_connection_id;
      if (!workspaceId || !connId) return '';
      return `https://airbyte-admin.avidai.com/workspaces/${workspaceId}/connections/${connId}/status`;
    },

    canSync(conn) {
      // Same rules as settings/ConnectionsSettingsSystems.vue
      // The SFTPs (disable)
      if (conn.integration_id == '7dbce608-4906-4606-977c-32ab1259b2e6' || conn.integration_id == '397b5b86-e954-495f-8fdb-513885f53251')
        return false;
      // File upload or File Forwarding(disable)
      if (conn.integration_id == '71b0c3b0-8390-49ec-9984-1c04b7384d38' || conn.integration_id == '323ce508-d21b-44ec-8064-5623e498ad13')
        return false;
      // If it is still unconfigured...
      if (conn.system_account_id == null)
        return false;
      return conn.active_jobs == null || conn.active_jobs.length == 0;
    },
    rerunDbtModel(connection_id){
      window.axios.post('/api/admin/connections/run_dbt', { connection_id: connection_id })
        .then(response => {
          this.showNotification({
            text: 'The data model will be re-run momentarily!'
          });
        })
        .catch(error => {
          window.Swal && Swal.fire({
            title: 'Error!',
            text: error?.response?.data?.message || 'Unable to re-run dbt model.',
            icon: 'error',
            customClass: { popup: 'swal-error' }
          });
        });
    },

    showImportErrors() {
      // Apply the import error filter
      this.filters.import_status = 'error';
      this.handleFilterSelection();
    },

    initializeFilterOptions() {
      // Get filter options from the backend
      window.axios.get('/api/admin/connections/filter_options')
        .then(response => {
          this.available_clients = response.data.clients;
          this.available_integrations = response.data.integrations;
          this.available_sync_schedules = response.data.sync_schedules;
          this.available_users = response.data.creators;
          this.available_import_statuses = response.data.import_statuses;
          this.available_export_statuses = response.data.export_statuses;
        })
        .catch(error => {
          console.error('Error loading filter options:', error);
          // Fallback to building from existing data
          this.buildFilterOptionsFromData();
        });
    },

    buildFilterOptionsFromData() {
      const clientsMap = new Map();
      const integrationsMap = new Map();
      const schedulesSet = new Set();
      const usersMap = new Map();

      this.connections.forEach(conn => {
        if (conn.client) clientsMap.set(conn.client.id, { id: conn.client.id, name: conn.client.name });
        if (conn.integration) integrationsMap.set(conn.integration.id, { id: conn.integration.id, name: conn.integration.name, display_name: conn.integration.display_name });
        if (conn.sync_schedule) schedulesSet.add(conn.sync_schedule);
        if (conn.created_by) usersMap.set(conn.created_by.id, { id: conn.created_by.id, name: conn.created_by.name });
      });

      this.available_clients = Array.from(clientsMap.values()).sort((a,b) => a.name.localeCompare(b.name));
      this.available_integrations = Array.from(integrationsMap.values()).sort((a,b) => (a.display_name || a.name).localeCompare(b.display_name || b.name));
      this.available_sync_schedules = Array.from(schedulesSet.values()).map(s => ({ id: s, name: this.propercase(s) })).sort((a,b) => a.name.localeCompare(b.name));
      this.available_users = Array.from(usersMap.values()).sort((a,b) => a.name.localeCompare(b.name));
      this.available_import_statuses = [
        { id: 'active', name: 'Active' },
        { id: 'error', name: 'Error' }
      ];
      this.available_export_statuses = [
        { id: 'active', name: 'Active' },
        { id: 'error', name: 'Error' }
      ];
    },

    handleFilterSelection() {
      // Reset to first page when filters change
      this.page_index = 0;

      // Check if we have any active filters or search term
      const hasFilters = this.filters.client_id || this.filters.integration_id || 
                        this.filters.sync_schedule || this.filters.import_status || 
                        this.filters.export_status || this.filters.created_by_id ||
                        this.filters.date_mismatch;
      
      const hasSearchTerm = this.search_key.trim() !== '';

      // If no filters and no search term, use original connection list
      if (!hasFilters && !hasSearchTerm) {
        this.filtered_connections = [...this.connections];
        this.local_num_connections = this.num_connections;
        this.reloadPagination();
        return;
      }

      // For small datasets without search term, filter locally
      if (!hasSearchTerm && this.num_connections < 100) {
        this.filtered_connections = this.connections.filter(connection => {
          // Client filter
          if (this.filters.client_id && String(connection.client?.id) !== String(this.filters.client_id)) return false;
          // Integration filter
          if (this.filters.integration_id && String(connection.integration?.id) !== String(this.filters.integration_id)) return false;
          // Sync schedule filter
          if (this.filters.sync_schedule && String(connection.sync_schedule) !== String(this.filters.sync_schedule)) return false;
          // Import Status filter (active|error)
          if (this.filters.import_status === 'active' && !!connection.sync_error_msg) return false;
          if (this.filters.import_status === 'error' && !connection.sync_error_msg) return false;

          // Export Status filter (Pathways/Census)
          const exportHasError = this.hasPathwayError(connection);
          if (this.filters.export_status === 'active' && exportHasError) return false;
          if (this.filters.export_status === 'error' && !exportHasError) return false;
          // Created by filter
          if (this.filters.created_by_id && String(connection.created_by?.id) !== String(this.filters.created_by_id)) return false;

          // Date mismatch filter
          if (this.filters.date_mismatch) {
            const syncDate = connection.last_synced_at ? new Date(connection.last_synced_at) : null;
            const refreshDate = connection.client?.last_sync_date ? new Date(connection.client.last_sync_date) : null;
            
            if (this.filters.date_mismatch === 'mismatch') {
              // Check if dates are different (considering null values)
              if (syncDate && refreshDate) {
                if (syncDate.getTime() === refreshDate.getTime()) return false;
              } else if (!syncDate && !refreshDate) {
                return false; // Both null, so they match
              }
            } else if (this.filters.date_mismatch === 'match') {
              // Check if dates are the same
              if (syncDate && refreshDate) {
                if (syncDate.getTime() !== refreshDate.getTime()) return false;
              } else if ((syncDate && !refreshDate) || (!syncDate && refreshDate)) {
                return false; // One has date, other doesn't, so they don't match
              }
            }
          }

          return true;
        });
        this.local_num_connections = this.filtered_connections.length;
        this.reloadPagination();
      } else {
        // Use server-side filtering for large datasets or when search is involved
        this.getFromDB();
      }

      this.$nextTick(() => {
        this.initializeTooltips();
      });
    },
    sortData() {
      const key = this.sort_key;
      this.filtered_connections.sort((a, b) => {
        let valA = a[key] ?? '';
        let valB = b[key] ?? '';

        // Derived sort keys
        if (key === 'client_name') {
          valA = a.client?.name || '';
          valB = b.client?.name || '';
        }
        if (key === 'integration_name') {
          valA = a.integration?.display_name || a.integration?.name || '';
          valB = b.integration?.display_name || b.integration?.name || '';
        }
        if (key === 'created_by_name') {
          valA = a.created_by?.name || '';
          valB = b.created_by?.name || '';
        }
        if (key === 'client_last_sync_date') {
          valA = a.client?.last_sync_date || '';
          valB = b.client?.last_sync_date || '';
        }
        if (key === 'sync_schedule') {
          // Normalize to lowercase for case-insensitive compare
          valA = (a.sync_schedule || '').toString().toLowerCase();
          valB = (b.sync_schedule || '').toString().toLowerCase();
        }



        // Date fields
        if (key === 'last_synced_at' || key === 'client_last_sync_date') {
          const dateA = valA ? new Date(valA) : null;
          const dateB = valB ? new Date(valB) : null;
          if (!dateA && !dateB) return 0;
          if (!dateA) return this.sort_order === 'asc' ? -1 : 1;
          if (!dateB) return this.sort_order === 'asc' ? 1 : -1;
          return this.sort_order === 'asc' ? dateA - dateB : dateB - dateA;
        }

        // String comparison
        if (typeof valA === 'string' && typeof valB === 'string') {
          return this.sort_order === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
        }

        // Generic comparison
        if (valA < valB) return this.sort_order === 'asc' ? -1 : 1;
        if (valA > valB) return this.sort_order === 'asc' ? 1 : -1;
        return 0;
      });
      this.reloadPagination();
    },

    sortByKey(key) {
      if (this.sort_key === key) {
        this.sort_order = this.sort_order === 'asc' ? 'des' : 'asc';
      } else {
        this.sort_key = key;
        this.sort_order = 'asc';
      }
      
      // Reset to first page when sorting changes
      this.page_index = 0;
      
      // Check if we should use server-side or client-side sorting
      if (this.hasActiveFilters() || this.search_key || this.local_num_connections >= 100) {
        // Use server-side sorting for filtered/searched/large datasets
        this.getFromDB();
      } else {
        // Use client-side sorting for small unfiltered datasets
        this.sortData();
      }
    },
    initializeTooltips() {
      // Destroy existing tooltips first
      const tooltipElements = document.querySelectorAll('[data-bs-toggle="tooltip"]');

      tooltipElements.forEach(element => {
        const tooltip = bootstrap.Tooltip.getInstance(element);
        if (tooltip) {
          tooltip.dispose();
        }
      });

      // Initialize tooltips for all elements with data-bs-toggle="tooltip"
      tooltipElements.forEach(element => {
        new bootstrap.Tooltip(element, {
          placement: 'top',
          trigger: 'hover',
          html: true,
          delay: { show: 0, hide: 100 }
        });
      });
    },

    integrationTooltip(connection) {
      const name = (connection.integration?.display_name || connection.integration?.name || '').toString();
      const isFileLike = name.toLowerCase() === 'file' || name.toUpperCase().includes('SFTP');
      if (isFileLike && connection.last_file) {
        const fname = connection.last_file.name || 'Unknown file';
        const fdate = connection.last_file.created_at ? this.datetime(connection.last_file.created_at) : '';
        return `Last upload: ${fname}${fdate ? ' — ' + fdate : ''}`;
      }
      return name;
    },

    hasPathwayError(connection) {
      if (!connection) return false;
      // Only count census/pathway errors if census_connections and syncs with failed status exist
      return connection.census_connections && connection.census_connections.some(censusConn =>
        censusConn.syncs && censusConn.syncs.some(sync => sync.last_sync_status === 'failed' && sync.last_sync_error_message)
      );
    },

    hasPathway(connection) {
      return connection.census_destination_id && connection.census_destination_objects && connection.census_destination_objects.length > 0;
    },

    pathwayErrorTooltip(connection) {
      if (!connection || !connection.census_connections) return '';
      for (const censusConn of connection.census_connections) {
        if (censusConn.syncs) {
          for (const sync of censusConn.syncs) {
            if (sync.last_sync_status === 'failed' && sync.last_sync_error_message) {
              return sync.last_sync_error_message;
            }
          }
        }
      }
      return '';
    },

    clearAllFilters() {
      // Reset all filter values
      this.filters = {
        client_id: '',
        integration_id: '',
        sync_schedule: '',
        import_status: '',
        export_status: '',
        created_by_id: '',
        date_mismatch: ''
      };
      
      // Trigger filter selection to refresh the data
      this.handleFilterSelection();
    },

    hasActiveFilters() {
      return this.filters.client_id || 
             this.filters.integration_id || 
             this.filters.sync_schedule || 
             this.filters.import_status || 
             this.filters.export_status || 
             this.filters.created_by_id ||
             this.filters.date_mismatch;
    },

    /* ------------------------------
     * Server-side search & pagination helpers
     * ------------------------------ */
    reloadPagination() {
      // Show first page of currently filtered connections
      this.list_shown = this.filtered_connections.slice(0, Math.min(this.num_per_page, this.filtered_connections.length));
      this.page_count = Math.ceil(this.local_num_connections / this.num_per_page);
      this.page_index_array = Array.from({ length: this.page_count }, (_, i) => i + 1);
      this.getItemInterval();
    },
    getItemInterval() {
      if (this.local_num_connections > 0) {
        const from = this.page_index * this.num_per_page + 1;
        let to = (this.page_index + 1) * this.num_per_page;
        if (to > this.local_num_connections) to = this.local_num_connections;
        this.item_interval = from >= to ? `${to}` : `${from} - ${to}`;
      } else {
        this.item_interval = '0';
      }
    },
    getFromDB() {
      const params = {
        pattern: '%' + this.search_key + '%',
        page_size: this.num_per_page,
        offset: this.num_per_page * this.page_index,
        sort_key: this.sort_key,
        sort_order: this.sort_order
      };

      // Add filter parameters
      if (this.filters.client_id) params.client_id = this.filters.client_id;
      if (this.filters.integration_id) params.integration_id = this.filters.integration_id;
      if (this.filters.sync_schedule) params.sync_schedule = this.filters.sync_schedule;
      if (this.filters.import_status) params.import_status = this.filters.import_status;
      if (this.filters.export_status) params.export_status = this.filters.export_status;
      if (this.filters.created_by_id) params.created_by_id = this.filters.created_by_id;
      if (this.filters.date_mismatch) params.client_last_sync_date_mismatch = this.filters.date_mismatch;

      window.axios.post('/api/admin/connections/list_by_pattern', params)
        .then(response => {
          this.filtered_connections = response.data.connections;
          this.local_num_connections = response.data.num_connections;
          this.local_num_errors = response.data.num_errors;
          this.reloadPagination();
        });
    },
    moveToPrevPage() {
      if (this.page_index > 0) {
        this.page_index--;
        this.getFromDB();
      }
    },
    moveToPage(page) {
      this.page_index = page - 1;
      this.getFromDB();
    },
    moveToNextPage() {
      if (this.page_index < this.page_count - 1) {
        this.page_index++;
        this.getFromDB();
      }
    },
    searchConnections() {
      // Reset to first page
      this.page_index = 0;

      // Check if we have any active filters
      const hasFilters = this.filters.client_id || this.filters.integration_id || 
                        this.filters.sync_schedule || this.filters.import_status || 
                        this.filters.export_status || this.filters.created_by_id ||
                        this.filters.date_mismatch;

      if (this.search_key === '' && !hasFilters) {
        this.filtered_connections = [...this.connections];
        this.local_num_connections = this.num_connections;
        this.reloadPagination();
      } else { // Large data set or has filters – fetch from server
        this.getFromDB();
      }
    }
  }
}
</script>


